import { currentUserId } from '@/data/auth'
import type { ProofParent } from '@/data/attachments'
import { enqueuePhoto } from './queue'
import { centralStoragePaths, storagePaths } from './pure'
import type { PreparedPhoto } from './compress'

/**
 * Put a prepared photo on the upload queue for a record that is ALREADY saved (an expense's
 * receipt, a remittance's deposit slip, the church treasury's count sheet). `date` is the record's
 * business date 'YYYY-MM-DD'. The church treasury's own proofs take no department.
 */
export async function queueProofPhoto(
  photo: PreparedPhoto,
  target: { departmentId: string | null; parent: ProofParent; date: string },
): Promise<void> {
  const id = crypto.randomUUID()
  let paths: { file: string; thumb: string }
  if (target.parent.kind === 'incomeSession' || target.parent.kind === 'incomeTransfer') {
    paths = centralStoragePaths({
      subject: target.parent.kind === 'incomeSession' ? 'income' : 'transfer',
      date: target.date,
      id,
      mime: photo.mime,
    })
  } else {
    if (!target.departmentId) throw new Error('A department is needed for this photo')
    paths = storagePaths({
      departmentId: target.departmentId,
      subject: { expense: 'expenses', movement: 'remittances', sheet: 'sheets' }[target.parent.kind] as 'expenses' | 'remittances' | 'sheets',
      date: target.date,
      id,
      mime: photo.mime,
    })
  }
  await enqueuePhoto({
    id,
    userId: await currentUserId(),
    departmentId: target.parent.kind === 'incomeSession' || target.parent.kind === 'incomeTransfer' ? null : target.departmentId,
    parent: target.parent,
    blob: photo.blob,
    thumb: photo.thumb,
    mime: photo.mime,
    sizeBytes: photo.sizeBytes,
    sha256: photo.sha256,
    storagePath: paths.file,
    thumbPath: paths.thumb,
  })
}
